// tslint:disable:ban-types
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injector } from '@angular/core';
import { resolve } from '@util/resource/resolve';
import { clone as _clone, merge as _merge } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface Resource {
  headers(headers: { [header: string]: string | string[] }): Resource;
  params(qp: { [param: string]: string | string[] | unknown }): Resource;
  basic(userOrToken: string, password?: string): Resource;
  bearer(token: string): Resource;
  authorization(type: "bearer" | "basic" | string, token: string): Resource;

  get<T>(response?: "json"): Observable<T>;
  get(response?: "text"): Observable<string>;
  get(response?: "blob"): Observable<Blob>;
  get(response?: "arraybuffer"): Observable<ArrayBuffer>;

  post<T>(
    body: any,
    options: { observe: "response" }
  ): Observable<HttpResponse<T>>;
  post<T>(body: any, options: { observe: "body" }): Observable<T>;
  post<T>(body?: any): Observable<T>;
  postText(body?: any): Observable<string>;
  postBlob(body?: any): Observable<HttpResponse<Blob>>;

  put<T>(body?: any): Observable<T>;
  patch<T>(body?: any): Observable<T>;
  head(): Observable<any>;
  delete(): Observable<any>;
  resolve(): string;
}

class ResourceImplementation implements Resource {
  private readonly path: string;
  private authorizationToken: string = "";
  private queryParams: { [k: string]: string | string[] } = {};
  private headerParams: { [k: string]: string | string[] } = {};
  private static readonly ENCODE_MAP: Record<string, string> = { "+": "%2B", "|": "%7C" };
  // @ts-ignore
  get http(): HttpClient {
    const injector: Injector = (window as any)["injector"];
    if (!injector) {
      throw new Error("Angular injector is not available. The application may not be fully initialized yet.");
    }
    return injector.get(HttpClient);
  }

  constructor(path: string) {
    this.path = path;
  }

  authorization(type: "bearer" | "basic" | string, token: string) {
    type = type[0].toUpperCase() + type.substr(1).toLowerCase();
    this.authorizationToken = `${type} ${token}`;
    return this;
  }

  basic(userOrToken: string, password?: string): Resource {
    if (password === undefined) return this.authorization("basic", userOrToken);
    else return this.authorization("basic", btoa(`${userOrToken}:${password}`));
  }

  bearer(token: string): Resource {
    return this.authorization("bearer", token);
  }

  delete(): Observable<any> {
    return this.http.delete(this.makeUrl(), { headers: this.makeHeaders() });
  }

  get<T>(response?: "json"): Observable<T>;
  get(response?: "text"): Observable<string>;
  get(response?: "blob"): Observable<Blob>;
  get(response?: "arraybuffer"): Observable<ArrayBuffer>;
  get<T = any>(
    responseType?: "json" | "text" | "blob" | "arraybuffer"
  ):
    | Observable<T>
    | Observable<string>
    | Observable<Blob>
    | Observable<ArrayBuffer> {
    const headers = this.makeHeaders(),
      opts = { headers };

    switch (responseType) {
      case "blob":
        return this.http.get(this.makeUrl(), {
          ...opts,
          responseType,
        }) as Observable<Blob>;
      case "arraybuffer":
        return this.http.get(this.makeUrl(), {
          ...opts,
          responseType,
        }) as Observable<ArrayBuffer>;
      case "text":
        return this.http.get(this.makeUrl(), {
          ...opts,
          responseType,
        }) as Observable<string>;
      default:
        return this.http.get<T>(this.makeUrl(), {
          ...opts,
          responseType: "json",
        }) as Observable<T>;
    }
  }

  head(): Observable<any> {
    return this.http.head(this.makeUrl(), { headers: this.makeHeaders() });
  }

  headers(headers: { [p: string]: string | string[] }): Resource {
    this.headerParams = _clone(headers);
    return this;
  }

  params(p: { [p: string]: string | string[] }): Resource {
    this.queryParams = _clone(p);
    return this;
  }

  post<T>(
    body?: any,
    options: { observe: "response" | "body" | "blob" } = { observe: "body" }
  ): Observable<T | HttpResponse<T>> {
    return this.http
      .post<T>(this.makeUrl(), body, {
        headers: this.makeHeaders(),
        observe: "response",
      })
      .pipe(
        map((response) => {
          if (options.observe == "body") return response.body as T;
          else return response;
        })
      );
  }
  postBlob(body?: any): Observable<HttpResponse<Blob>> {
    return this.http.post(this.makeUrl(), body, {
      headers: this.makeHeaders(),
      observe: "response",
      responseType: "blob",
    }) as Observable<HttpResponse<Blob>>;
  }

  postText(body?: any): Observable<string> {
    return this.http.post(this.makeUrl(), body, {
      headers: this.makeHeaders(),
      responseType: "text",
    }) as Observable<string>;
  }

  put<T>(body?: any): Observable<T> {
    return this.http.put<T>(this.makeUrl(), body, {
      headers: this.makeHeaders(),
    });
  }

  patch<T>(body?: any): Observable<T> {
    return this.http.patch<T>(this.makeUrl(), body, {
      headers: this.makeHeaders(),
    });
  }

  resolve(): string {
    return this.makeUrl();
  }

  private makeUrl() {
    if (!!!this.path) {
      throw new Error(
        "Resource path is undefined - Remember to add path " +
          this.path +
          " to environment.common file"
      );
    } else {
      try {
        const encodedParams = this.encodeParams(this.queryParams);
        return resolve(this.path, encodedParams);
      } catch (err) {
        throw new Error(
          "Resource path is undefined - Remember to add path " +
            this.path +
            " to environment.common file"
        );
      }
    }
  }

  private makeHeaders(): HttpHeaders {
    if (!this.headerParams) return new HttpHeaders();
    const headers = Object.keys(this.headerParams).reduce((h, k) => {
      return h.append(k, this.headerParams[k]);
    }, new HttpHeaders());
    if (this.authorizationToken)
      return headers.append("Authorization", this.authorizationToken);
    else return headers;
  }

  private encodeParams(params: { [k: string]: string | string[] }) {
    const encoded: { [k: string]: string | string[] } = _merge({}, params);
    Object.keys(encoded).forEach((key) => {
      const value = encoded[key];
      if (Array.isArray(value)) {
        encoded[key] = value.map((v) => this.encodeReserved(v));
      } else {
        encoded[key] = this.encodeReserved(value);
      }
    });
    return encoded;
  }

  private encodeReserved(value: string) {
    let encoded = `${value ?? ""}`;
    Object.keys(ResourceImplementation.ENCODE_MAP).forEach((reserved) => {
      encoded = encoded.split(reserved).join(ResourceImplementation.ENCODE_MAP[reserved]);
    });
    return encoded;
  }
}

function resource(path: string): Resource {
  return new ResourceImplementation(path);
}

export { resource };